import React, { FC, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useChangeCampaignNumberMutation, useGetAllCampaignsQuery } from '../../api/campaignAPI';
import Form from '../../components/Form/Form';
import Icon from '../../components/Icon/Icon';
import { P } from '../../components/Text/Text';
import { IconColorE, IconSizeE } from '../../models/enums';
import { FormFieldT } from '../../models/models';
import usePopup from '../../utils/hooks/usePopup';

import styles from './styles.module.scss';

type ChangeCampaignNumberBodyPropTypes = {
    projectId: number;
    currentCampaignNumber: number;
}

const ChangeCampaignNumberBody: FC<ChangeCampaignNumberBodyPropTypes> = (props) => {
    const { projectId, currentCampaignNumber } = props;

    const { removePopup } = usePopup();
    const [changeCampaignNumber] = useChangeCampaignNumberMutation();
    const { data: campaignsSO = [], isLoading } = useGetAllCampaignsQuery({ projectId, asSelectOptions: true }, { skip: !projectId });
    const fields: FormFieldT[] = useMemo(() => [
        {
            label: 'Campaign',
            selectOptions: campaignsSO,
            defaultValue: campaignsSO.find(c => +(c.label.split(' ')[0]) === +currentCampaignNumber),
        },
    ], [campaignsSO, currentCampaignNumber]);

    const handleSubmit = async (data: any) => {
        const body = {
            CampaignId: data.campaign.value.split('-')[2],
            Id: projectId,
        };

        const res: any = await changeCampaignNumber(body);
        if (res?.data?.Id) {
            toast.success('Campaign number changed successfully');
            removePopup();
        } else {
            toast.error('Something went wrong');
        }
    };

    return (
        <div>
            {isLoading ? (
                // Still fetching the reassignment targets — show the spinner only while the request
                // is in flight, never for a resolved-but-empty list (that was the "stuck on loading"
                // bug: an empty result was indistinguishable from loading).
                <div className={styles.loadingWrapper}>
                    <Icon name='loading' size={IconSizeE.S} color={IconColorE.GRAY} />
                </div>
            ) : !!campaignsSO.length ? (
                <>
                    <Form fields={fields} formID='select-campaign' onSubmit={handleSubmit} columns={1} />
                    <P className={styles.broadcastDateNote}>
                        This delivery can be associated with campaigns whose creation date was within the last two months.
                    </P>
                </>
            ) : (
                // Loaded, but no campaign matches (same advertiser & brand, active, created in the last
                // two months) — tell the user instead of spinning forever.
                <P className={styles.noCampaignsNote}>
                    There is no past campaign this delivery can be associated with. A matching campaign must have the same advertiser and brand, be active, and have been created within the last two months.
                </P>
            )}
        </div>
    );
};

export default ChangeCampaignNumberBody;